import { State } from "../state";
import { AchievementInfoTotal } from "./AchievementInfoTotal";
import { AchievementInfo } from "./AchievementInfoTypes";

/**
 * Applies achievement checking to a state object and returns a new state with updated achievements
 * Should be called at the end of any action handler that modifies achievement-related stats
 */
export const applyAchievementChecks = (state: State): State => {
  const newUnlockedAchievements = checkAchievements(state);

  // Only return new state if achievements changed
  if (newUnlockedAchievements.size === state.unlockedAchievements.size) {
    return state;
  }

  return {
    ...state,
    unlockedAchievements: newUnlockedAchievements,
  };
};

/**
 * Checks all achievements and returns a new Set of unlocked achievement IDs
 * Should be called whenever any achievement-related stats change
 */
export const checkAchievements = (state: State): Set<string> => {
  const newUnlockedAchievements = new Set<string>(state.unlockedAchievements);

  AchievementInfoTotal.forEach((achievement: AchievementInfo, id: string) => {
    // Skip if already unlocked
    if (newUnlockedAchievements.has(id)) {
      return;
    }

    // Check if achievement should be unlocked
    const shouldUnlock = checkSingleAchievement(achievement, state);

    if (shouldUnlock) {
      newUnlockedAchievements.add(id);
    }
  });

  return newUnlockedAchievements;
};

/**
 * Checks if a single achievement should be unlocked based on current state
 */
const checkSingleAchievement = (
  achievement: AchievementInfo,
  state: State,
): boolean => {
  switch (achievement.category) {
    case "total":
      return checkTotalAchievement(achievement, state);

    case "brick":
      return checkBrickAchievement(achievement, state);

    case "action":
      return checkActionAchievement(achievement, state);

    case "brickpack":
      return checkBrickPackAchievement(achievement, state);

    case "set":
      return checkSetAchievement(achievement, state);

    default:
      return false;
  }
};

/**
 * Check total-based achievements (total bricks, total actions, total sets)
 */
const checkTotalAchievement = (
  achievement: AchievementInfo,
  state: State,
): boolean => {
  // Determine which total stat to check based on achievement ID pattern
  if (achievement.id.includes("total_bricks")) {
    return state.totalBricksCollected >= achievement.threshold;
  }

  if (achievement.id.includes("total_actions")) {
    return state.totalActionsTriggered >= achievement.threshold;
  }

  if (achievement.id.includes("total_sets")) {
    return state.totalSetsPurchased >= achievement.threshold;
  }

  return false;
};

/**
 * Check brick-specific achievements
 */
const checkBrickAchievement = (
  achievement: AchievementInfo,
  state: State,
): boolean => {
  if (!achievement.targetId) return false;

  const brickCount = state.bricksCollectedByType.get(achievement.targetId) || 0;
  return brickCount >= achievement.threshold;
};

/**
 * Check action-specific achievements
 */
const checkActionAchievement = (
  achievement: AchievementInfo,
  state: State,
): boolean => {
  if (!achievement.targetId) return false;

  const actionCount =
    state.actionsTriggeredByType.get(achievement.targetId) || 0;
  return actionCount >= achievement.threshold;
};

/**
 * Check brick pack-specific achievements
 */
const checkBrickPackAchievement = (
  achievement: AchievementInfo,
  state: State,
): boolean => {
  if (!achievement.targetId) return false;

  const packCount = state.brickPackStats.get(achievement.targetId) || 0;
  return packCount >= achievement.threshold;
};

/**
 * Check set-specific achievements
 */
const checkSetAchievement = (
  achievement: AchievementInfo,
  state: State,
): boolean => {
  if (!achievement.targetId) return false;

  const setCount = state.setsPurchasedByType.get(achievement.targetId) || 0;
  return setCount >= achievement.threshold;
};
